import { useRouter } from 'next/router';
import { ChangeEvent, useEffect, useState } from 'react';
import { TextField } from '@mui/material';
import {
  isEqual, isNumber, omit,
} from 'lodash';
import {
  clearQuery, updateRouterQuery,
} from 'lib/utils/filterUtils';
import { FilterPopover } from 'components/FilterPopover';

export interface FollowersCriteria {
  minFollowers?: number;
  maxFollowers?: number;
}

interface Props {
  followersRange: FollowersCriteria;
}

export const FollowersFilter = ({ followersRange }: Props) => {
  const router = useRouter();
  const [values, setValues] = useState<FollowersCriteria>(followersRange);

  useEffect(() => {
    setValues(followersRange);
  }, [followersRange]);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const targetValue = parseInt(event.target.value, 10);
    const isValidValue = isNumber(targetValue) && targetValue >= 0;
    if (isValidValue) {
      setValues({
        ...values,
        [event.target.name]: targetValue,
      });
      return;
    }

    setValues(omit(values, event.target.name));
  };

  const onApply = () => {
    if (!isEqual(followersRange, values)) {
      const q = Object.keys(values).reduce(
        (acc, key) => {
          acc[key] = values[key as keyof FollowersCriteria];
          return acc;
        }, {} as any,
      );

      const query = {
        ...clearQuery(Object.keys(followersRange)),
        ...q,
      };
      updateRouterQuery(router, query);
    }
  };

  const onClear = () => {
    setValues({});

    const q = Object.keys(followersRange).reduce(
      (acc, key) => {
        acc[key] = [];
        return acc;
      }, {} as any,
    );
    updateRouterQuery(router, {
      ...q,
    });
  };

  const getTitle = () => {
    const minFollowers = values.minFollowers ? Number(values.minFollowers) : undefined;
    const maxFollowers = values.maxFollowers ? Number(values.maxFollowers) : undefined;

    if (!!maxFollowers && !!minFollowers) {
      return `${minFollowers.toLocaleString()} - ${maxFollowers.toLocaleString()} followers`;
    }

    if (!minFollowers && !!maxFollowers) {
      return `Up to ${maxFollowers.toLocaleString()} followers`;
    }

    if (!maxFollowers && !!minFollowers) {
      return `At least ${minFollowers.toLocaleString()} followers`;
    }

    return 'Followers';
  };

  return (
    <FilterPopover
      title={getTitle()}
      onApply={onApply}
      onClear={onClear}
      clearable={Object.keys(values).length > 0}
    >
      <div className="mb-2 items-center">
        <h6 className="font-semibold mb-1">
          Total Followers
        </h6>
        <div className="flex gap-4 justify-between">
          <TextField
            label="min"
            variant="outlined"
            type="number"
            InputProps={{ inputProps: { min: 0 } }}
            name="minFollowers"
            onChange={onChange}
            className="w-full"
            value={values.minFollowers || ''}
            size="small"
          />
          <TextField
            label="max"
            variant="outlined"
            type="number"
            InputProps={{ inputProps: { min: 0 } }}
            name="maxFollowers"
            onChange={onChange}
            className="w-full"
            value={values.maxFollowers || ''}
            size="small"
          />
        </div>
      </div>
    </FilterPopover>
  );
};
